'use client';

import { useEffect, useRef, useState } from 'react';
import { createWorker, OEM, PSM } from 'tesseract.js';
import { getToolAuthToken } from '@/lib/browser-tool-usage';

type OcrImageSource = {
  id: string;
  file: File;
  name: string;
  size: number;
  width: number;
  height: number;
  previewUrl: string;
};

type OcrProgress = {
  label: string;
  progress: number;
};

type TesseractWorkerLike = Awaited<ReturnType<typeof createWorker>>;

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function formatExportTimestamp(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}${month}${day}-${hours}${minutes}`;
}

function createTextFileName() {
  return `cleanor.app-ocr-image-to-text-${formatExportTimestamp(new Date())}.txt`;
}

function isImageFile(file: File) {
  return file.type.startsWith('image/');
}

function formatDimensions(width: number, height: number) {
  return `${width} × ${height}`;
}

function normalizeRecognizedText(text: string) {
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

function createTextBlob(text: string) {
  return new Blob([text], { type: 'text/plain;charset=utf-8' });
}

function triggerBrowserDownload(url: string, fileName: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

function formatOcrStatus(status: string) {
  switch (status) {
    case 'loading tesseract core':
      return 'Loading OCR engine…';
    case 'initializing tesseract':
      return 'Initializing OCR engine…';
    case 'loading language traineddata':
      return 'Loading language data…';
    case 'initializing api':
      return 'Preparing OCR worker…';
    case 'recognizing text':
      return 'Recognizing text…';
    default:
      return status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}…` : 'Processing…';
  }
}

async function readImageSource(file: File): Promise<OcrImageSource> {
  const previewUrl = URL.createObjectURL(file);

  try {
    const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        resolve({
          width: image.naturalWidth,
          height: image.naturalHeight,
        });
      };
      image.onerror = () => reject(new Error('This image could not be decoded in the browser.'));
      image.src = previewUrl;
    });

    return {
      id: crypto.randomUUID(),
      file,
      name: file.name,
      size: file.size,
      width: dimensions.width,
      height: dimensions.height,
      previewUrl,
    };
  } catch (error) {
    URL.revokeObjectURL(previewUrl);
    throw error;
  }
}

export default function BrowserOcrImageToText() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const workerRef = useRef<TesseractWorkerLike | null>(null);
  const [source, setSource] = useState<OcrImageSource | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [progress, setProgress] = useState<OcrProgress | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [recognizedText, setRecognizedText] = useState('');
  const [confidence, setConfidence] = useState<number | null>(null);
  const [wordCount, setWordCount] = useState<number | null>(null);

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }

      void workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, [source]);

  function replaceSource(nextSource: OcrImageSource | null) {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return nextSource;
    });
  }

  function resetResultState() {
    setCreditMessage(null);
    setErrorMessage(null);
    setRecognizedText('');
    setConfidence(null);
    setWordCount(null);
    setProgress(null);
  }

  async function handleFileSelection(file: File | null) {
    resetResultState();

    if (!file) {
      replaceSource(null);
      return;
    }

    if (!isImageFile(file)) {
      replaceSource(null);
      setErrorMessage('Choose one image file to extract text from.');
      return;
    }

    try {
      const nextSource = await readImageSource(file);
      replaceSource(nextSource);
    } catch (error) {
      replaceSource(null);
      setErrorMessage(error instanceof Error ? error.message : 'This image could not be opened.');
    }
  }

  function clearSource() {
    replaceSource(null);
    resetResultState();

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  async function runOcr() {
    if (!source) {
      setErrorMessage('Choose an image first.');
      return;
    }

    setIsRecognizing(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setProgress({ label: 'Checking credits and preparing OCR…', progress: 0 });

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: 'ocr-image-to-text' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another OCR run right now.';
        throw new Error(message);
      }

      const worker = await createWorker('eng', OEM.LSTM_ONLY, {
        workerPath: '/tesseract-worker.min.js',
        corePath: '/tesseract-core-simd-lstm.wasm.js',
        logger: (message: { status: string; progress: number }) => {
          setProgress({
            label: formatOcrStatus(message.status),
            progress: Math.max(0, Math.min(1, message.progress ?? 0)),
          });
        },
      });
      workerRef.current = worker;

      await worker.setParameters({
        tessedit_pageseg_mode: PSM.AUTO,
        preserve_interword_spaces: '1',
      });

      const result = await worker.recognize(source.file);
      const nextText = normalizeRecognizedText(result.data.text ?? '');
      const nextConfidence =
        typeof result.data.confidence === 'number' ? Math.round(result.data.confidence) : null;
      const nextWordCount = nextText ? nextText.split(/\s+/).filter(Boolean).length : 0;

      setRecognizedText(nextText);
      setConfidence(nextConfidence);
      setWordCount(nextWordCount);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Text extraction finished. Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `Text extraction finished. ${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : 'Text extraction finished.',
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Text could not be extracted from this image.',
      );
    } finally {
      setProgress(null);
      setIsRecognizing(false);
      await workerRef.current?.terminate();
      workerRef.current = null;
    }
  }

  async function copyText() {
    if (!recognizedText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(recognizedText);
      setCreditMessage('Text copied to clipboard.');
      setErrorMessage(null);
    } catch {
      setErrorMessage('Clipboard access is blocked in this browser.');
    }
  }

  function downloadText() {
    if (!recognizedText) {
      return;
    }

    const blob = createTextBlob(recognizedText);
    const nextUrl = URL.createObjectURL(blob);
    triggerBrowserDownload(nextUrl, createTextFileName());
    URL.revokeObjectURL(nextUrl);
  }

  return (
    <article className="tool-live-card tool-pdf-card">
      <div className="tool-live-header">
        <div>
          <h2>Extract text from one image in the browser</h2>
          <p>
            Upload a screenshot, scan, receipt, note, or document photo, run OCR locally, and copy
            or download the recognized text without sending the image to your server.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? 'Image ready for OCR' : 'Choose one image or drop it here'}</strong>
              <span>
                {source
                  ? `${formatDimensions(source.width, source.height)} • ${formatFileSize(source.size)}`
                  : 'Works best with screenshots, scans, receipts, and document photos'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/*"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isRecognizing}
              onClick={() => void runOcr()}
              type="button"
            >
              {isRecognizing ? 'Extracting text…' : 'Extract text'}
            </button>
            {recognizedText ? (
              <button className="button button-ghost" onClick={() => void copyText()} type="button">
                Copy text
              </button>
            ) : null}
            {recognizedText ? (
              <button className="button button-ghost" onClick={downloadText} type="button">
                Download TXT
              </button>
            ) : null}
            {source ? (
              <button className="button button-ghost" onClick={clearSource} type="button">
                Clear file
              </button>
            ) : null}
          </div>

          {progress ? (
            <div className="tool-live-panel-note tool-pdf-progress" aria-live="polite">
              <strong>{progress.label}</strong>
              <progress max={100} value={Math.round(progress.progress * 100)} />
              <span>{`${Math.round(progress.progress * 100)}%`}</span>
            </div>
          ) : null}

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Client-side OCR extractor</h3>
          <div className="tool-mockup-chip-list">
            <span>Image → Text</span>
            <span>OCR</span>
            <span>Copy ready</span>
            <span>Local processing</span>
          </div>
          <p>
            This is built for one fast OCR job at a time: extract text, copy it, or download it as a
            plain text file and move on.
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>
              Clear screenshots and scan-style images work best. Dense handwriting and very noisy
              photos may produce incomplete text.
            </span>
          </div>
          {source ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source image</strong>
                <span>{formatDimensions(source.width, source.height)}</span>
              </div>
              <div>
                <strong>File size</strong>
                <span>{formatFileSize(source.size)}</span>
              </div>
              {confidence !== null ? (
                <div>
                  <strong>OCR confidence</strong>
                  <span>{`${confidence}%`}</span>
                </div>
              ) : null}
              {wordCount !== null ? (
                <div>
                  <strong>Recognized words</strong>
                  <span>{wordCount}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {source ? (
        <div className="tool-live-preview">
          <h3>Image preview</h3>
          <div className="tool-live-preview-frame">
            <img alt={source.name} src={source.previewUrl} />
          </div>
        </div>
      ) : null}

      {source ? (
        <div className="tool-live-preview">
          <h3>Recognized text</h3>
          <div className="tool-live-panel tool-ocr-output-shell">
            <textarea
              className="tool-ocr-output"
              placeholder="Run OCR to extract text from the current image."
              readOnly
              value={recognizedText}
            />
          </div>
        </div>
      ) : null}
    </article>
  );
}
